// Admin-console API client. Deliberately SEPARATE from client.ts so these
// endpoints are only bundled into the admin container (see AdminApp.tsx and
// vite.config.ts's BUILD_TARGET switch) and never shipped to a customer
// browser. Same origin, same session cookie, same ApiError semantics.

import { request } from './client';
import type {
  AdminBucket,
  AdminSnapshot,
  BurnRequest,
  AdminDashboard,
  AdminSearchHit,
  AdminUser,
  Archive,
  BreachAlert,
  BulkAction,
  BulkResult,
  EffectiveLimits,
  FlagMatrix,
  MigrationPreview,
  Page,
  Plan,
  PlanVersion,
  PlanVersionInput,
  PlanVersionSummary,
  Subscription,
  SubscriptionStatus,
  TenantSummary,
  TenantUsage,
  VerificationScheduleRow,
} from './types';

export const adminApi = {
  adminSearch: (q: string, limit: number, cursor?: string | null) =>
    request<Page<AdminSearchHit>>('/v1/admin/search', {}, { q, limit, cursor }),

  adminTenants: () => request<{ items: TenantSummary[] }>('/v1/admin/tenants'),

  adminTenantArchives: (tenantId: string, limit: number, cursor?: string | null) =>
    request<Page<Archive>>(
      `/v1/admin/tenants/${encodeURIComponent(tenantId)}/archives`,
      {},
      { limit, cursor },
    ),

  adminTenantEffectiveLimits: (tenantId: string) =>
    request<EffectiveLimits>(`/v1/admin/tenants/${encodeURIComponent(tenantId)}/effective-limits`),

  adminTenantUsage: (tenantId: string) =>
    request<TenantUsage>(`/v1/admin/tenants/${encodeURIComponent(tenantId)}/usage`),

  adminPlans: () => request<{ items: Plan[] }>('/v1/admin/plans'),

  adminAllPlanVersions: () => request<{ items: PlanVersionSummary[] }>('/v1/admin/plan-versions'),

  adminCreatePlan: (name: string, version: PlanVersionInput) =>
    request<Plan>('/v1/admin/plans', {
      method: 'POST',
      body: JSON.stringify({ name, version }),
    }),

  adminCreatePlanVersion: (planId: string, version: PlanVersionInput) =>
    request<PlanVersion>(`/v1/admin/plans/${encodeURIComponent(planId)}/versions`, {
      method: 'POST',
      body: JSON.stringify(version),
    }),

  adminSubscriptions: () => request<{ items: Subscription[] }>('/v1/admin/subscriptions'),

  adminAssignSubscription: (tenantId: string, planVersionId: string, overrides: Record<string, unknown>) =>
    request<Subscription>(`/v1/admin/subscriptions/${encodeURIComponent(tenantId)}`, {
      method: 'PUT',
      body: JSON.stringify({ planVersionId, overrides }),
    }),

  adminSetSubscriptionStatus: (tenantId: string, status: SubscriptionStatus) =>
    request<Subscription>(`/v1/admin/subscriptions/${encodeURIComponent(tenantId)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  // Phase 2 ------------------------------------------------------------
  adminMigrationPreview: (planId: string, toPlanVersionId: string) =>
    request<MigrationPreview>(
      `/v1/admin/plans/${encodeURIComponent(planId)}/migration-preview`,
      {},
      { to: toPlanVersionId },
    ),

  adminSubscriptionsBulk: (tenantIds: string[], action: BulkAction, planVersionId?: string) =>
    request<{ results: BulkResult[] }>('/v1/admin/subscriptions/bulk', {
      method: 'POST',
      body: JSON.stringify({ tenantIds, action, planVersionId }),
    }),

  adminAlerts: () => request<{ items: BreachAlert[] }>('/v1/admin/alerts'),

  adminImpersonate: (tenantId: string) =>
    request<{ tenantId: string; expiresInSeconds: number }>(
      `/v1/admin/tenants/${encodeURIComponent(tenantId)}/impersonate`,
      { method: 'POST' },
    ),


  // Phase 3 ------------------------------------------------------------
  adminFeatureFlags: (q?: string, overriddenOnly?: boolean) =>
    request<FlagMatrix>('/v1/admin/feature-flags', {}, { q, overriddenOnly: overriddenOnly || undefined }),

  /** Set one flag override for one tenant. Pass clear=true to remove it so
   *  the field falls back to its plan/features value. */
  adminSetFlagOverride: (tenantId: string, field: string, value: unknown, clear = false) =>
    request<Subscription>(`/v1/admin/tenants/${encodeURIComponent(tenantId)}/flag-override`, {
      method: 'PATCH',
      body: JSON.stringify({ field, value, clear }),
    }),

  adminVerificationSchedule: () =>
    request<{ items: VerificationScheduleRow[] }>('/v1/admin/verification-schedule'),

  // Buckets / users / dashboard -------------------------------------------
  adminDashboard: () => request<AdminDashboard>('/v1/admin/dashboard'),

  adminBuckets: (limit: number, cursor?: string | null, q?: string, scheduledOnly?: boolean) =>
    request<Page<AdminBucket>>('/v1/admin/buckets', {}, {
      limit, cursor, q, scheduledOnly: scheduledOnly || undefined,
    }),

  adminUsers: (limit: number, cursor?: string | null, q?: string) =>
    request<Page<AdminUser>>('/v1/admin/users', {}, { limit, cursor, q }),

  /** Every burn across all tenants, newest first. */
  adminSnapshots: (limit: number, cursor?: string | null, q?: string) =>
    request<Page<AdminSnapshot>>('/v1/admin/snapshots', {}, { limit, cursor, q }),

  // Burn approval queue -----------------------------------------------------
  adminBurnRequests: (limit: number, cursor?: string | null, pendingOnly?: boolean) =>
    request<Page<BurnRequest>>('/v1/admin/burn-requests', {}, {
      limit, cursor, pendingOnly: pendingOnly || undefined,
    }),

  /** Approves and starts the DMD build in the background. */
  adminApproveBurn: (id: string, note?: string) =>
    request<BurnRequest>(`/v1/admin/burn-requests/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: JSON.stringify({ note: note ?? '' }),
    }),

  /** A reason is required; the customer is shown it. */
  adminRejectBurn: (id: string, note: string) =>
    request<BurnRequest>(`/v1/admin/burn-requests/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  // Organisations / buckets: write operations ------------------------------
  adminCreateTenant: (id: string, name: string) =>
    request<{ ID: string; Name: string }>('/v1/admin/tenants', {
      method: 'POST',
      body: JSON.stringify({ id, name }),
    }),

  adminRenameTenant: (id: string, name: string) =>
    request<{ ID: string; Name: string }>(`/v1/admin/tenants/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  adminCreateBucket: (tenantId: string, name: string, retentionPolicy: string) =>
    request<Archive>(`/v1/admin/tenants/${encodeURIComponent(tenantId)}/archives`, {
      method: 'POST',
      body: JSON.stringify({ name, retentionPolicy }),
    }),

  adminUpdateBucket: (archiveId: string, retentionPolicy: string) =>
    request<Archive>(`/v1/admin/buckets/${encodeURIComponent(archiveId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ retentionPolicy }),
    }),

  adminDeleteBucket: (archiveId: string) =>
    request<void>(`/v1/admin/buckets/${encodeURIComponent(archiveId)}`, { method: 'DELETE' }),
};
